/**
 * Demo topologies — the canonical set of patterns shown in the simulated build,
 * plus the `generateTopology` builder shared with the in-app DemoModePanel.
 *
 * `generateTopology` returns a scene-shaped TopologyInfo (the same shape the
 * pixel-world canvas derives from a backend topology definition), so the demo
 * backend can serve it directly as a topology `definition`.
 */

import type { TopologyInfo } from "../pixel-world/scenes/SceneLayout.js";

export const TOPOLOGY_TYPES = [
  { value: "star", label: "Star" },
  { value: "pipeline", label: "Pipeline" },
  { value: "hierarchical", label: "Hierarchical" },
  { value: "mesh", label: "Mesh" },
  { value: "map-reduce", label: "Map-Reduce" },
  { value: "debate", label: "Debate" },
] as const;

export type TopologyType = (typeof TOPOLOGY_TYPES)[number]["value"];

const BACKENDS = ["claude-agent-sdk", "openai-agents", "cloudflare-agents", "raw-api"];

const AGENT_NAMES = [
  "Manager", "Researcher", "Analyst", "Writer", "Reviewer",
  "Planner", "Engineer", "Designer",
];

/** Smallest agent count a pattern needs to be well-formed. */
export function minAgentsFor(type: TopologyType): number {
  return type === "map-reduce" || type === "debate" ? 3 : 2;
}

/** Build a scene-shaped topology for the given pattern and agent count. */
export function generateTopology(type: TopologyType, count: number): TopologyInfo {
  const agents = Array.from({ length: count }, (_, i) => ({
    id: `agent-${i}`,
    name: AGENT_NAMES[i] ?? `Agent ${i + 1}`,
    backend: BACKENDS[i % BACKENDS.length],
  }));

  const edges: TopologyInfo["edges"] = [];

  switch (type) {
    case "star":
      for (let i = 1; i < count; i++) {
        edges.push({ from: agents[0].id, to: agents[i].id, type: "delegates-to", bidirectional: true });
        edges.push({ from: agents[i].id, to: agents[0].id, type: "reports-to" });
      }
      return { type: "star", agents, edges, entryPoint: agents[0].id };

    case "pipeline":
      for (let i = 0; i < count - 1; i++) {
        edges.push({ from: agents[i].id, to: agents[i + 1].id, type: "pipes-to" });
      }
      return { type: "pipeline", agents, edges, entryPoint: agents[0].id };

    case "hierarchical":
      for (let i = 1; i < count; i++) {
        const parent = Math.floor((i - 1) / 2);
        edges.push({ from: agents[parent].id, to: agents[i].id, type: "delegates-to" });
      }
      return { type: "hierarchical", agents, edges, entryPoint: agents[0].id };

    case "mesh":
      for (let i = 0; i < count; i++) {
        for (let j = i + 1; j < count; j++) {
          edges.push({ from: agents[i].id, to: agents[j].id, type: "peers-with", bidirectional: true });
        }
      }
      return { type: "mesh", agents, edges, entryPoint: agents[0].id };

    case "map-reduce": {
      const reducer = agents[count - 1];
      for (let i = 1; i < count - 1; i++) {
        edges.push({ from: agents[0].id, to: agents[i].id, type: "delegates-to" });
        edges.push({ from: agents[i].id, to: reducer.id, type: "reports-to" });
      }
      if (count === 2) {
        edges.push({ from: agents[0].id, to: agents[1].id, type: "delegates-to" });
      }
      return { type: "custom", agents, edges, entryPoint: agents[0].id, exitPoints: [reducer.id] };
    }

    case "debate": {
      const judge = agents[count - 1];
      for (let i = 0; i < count - 1; i++) {
        edges.push({ from: agents[i].id, to: judge.id, type: "reports-to" });
      }
      for (let i = 0; i < count - 1; i++) {
        for (let j = i + 1; j < count - 1; j++) {
          edges.push({ from: agents[i].id, to: agents[j].id, type: "peers-with", bidirectional: true });
        }
      }
      return { type: "custom", agents, edges, entryPoint: agents[0].id, exitPoints: [judge.id] };
    }
  }
}

// ---------------------------------------------------------------------------
// The fixed set of topologies presented in the simulated build.
// ---------------------------------------------------------------------------

export interface DemoTopologyMeta {
  /** Stable id used as the selected topology id in the world store. */
  id: string;
  label: string;
  type: TopologyType;
  agentCount: number;
}

export const DEMO_TOPOLOGIES: DemoTopologyMeta[] = [
  { id: "demo-star", label: "Star", type: "star", agentCount: 5 },
  { id: "demo-pipeline", label: "Pipeline", type: "pipeline", agentCount: 5 },
  { id: "demo-hierarchical", label: "Hierarchical", type: "hierarchical", agentCount: 7 },
  { id: "demo-mesh", label: "Mesh", type: "mesh", agentCount: 5 },
  { id: "demo-map-reduce", label: "Map-Reduce", type: "map-reduce", agentCount: 6 },
  { id: "demo-debate", label: "Debate", type: "debate", agentCount: 5 },
];

export const DEFAULT_DEMO_TOPOLOGY_ID = DEMO_TOPOLOGIES[0].id;

export function demoTopologyMeta(id: string): DemoTopologyMeta {
  return DEMO_TOPOLOGIES.find((t) => t.id === id) ?? DEMO_TOPOLOGIES[0];
}

/** The scene-shaped definition for a demo topology id. */
export function demoTopologyDefinition(id: string): TopologyInfo {
  const meta = demoTopologyMeta(id);
  return generateTopology(meta.type, meta.agentCount);
}
